import React from "react";
import { Shield, Dog, Cat, Bird } from "lucide-react";

/*
 * PhotoUnavailablePlaceholder
 * -----------------------------------------------------------------------------
 * A professional, intentional "no photo on file" panel for offender records
 * where PAWS on Record has not verified a photograph of the correct subject.
 *
 * DESIGN INTENT
 * - Never looks like a broken image or a missing asset.
 * - Never uses a human face or human silhouette (would be misread as offender).
 * - Uses subtle imagery representing the animal(s) named in the case so the
 *   panel still communicates "this record concerns a dog / cat / horse /
 *   livestock / multiple animals" while making the absence of a verified
 *   photo explicit.
 * - Reads at a glance: PUBLIC RECORD · PHOTO UNAVAILABLE.
 */

// ---- Custom SVG silhouettes ----
const HorseSilhouette = ({ size = 96 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 46 L12 38 Q12 30 18 26 L22 22 Q23 20 24 20 L30 18 L34 14 L38 12 L42 12 Q46 12 48 16 L50 22 Q50 24 48 24 L46 24 L48 30 L48 46" />
    <path d="M48 46 L48 52 L44 52 L44 46" />
    <path d="M12 46 L12 52 L16 52 L16 46" />
    <path d="M28 30 L28 46 L32 46 L32 30" />
    <circle cx="42" cy="18" r="1.5" fill="currentColor" />
    <path d="M38 14 L36 10" />
  </svg>
);

// Simple cow silhouette for cattle / general livestock
const LivestockSilhouette = ({ size = 96 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {/* body */}
    <path d="M10 42 Q10 28 22 26 L42 26 Q54 28 54 42 L54 46 L10 46 Z" />
    {/* legs */}
    <path d="M16 46 L16 54" />
    <path d="M24 46 L24 54" />
    <path d="M40 46 L40 54" />
    <path d="M48 46 L48 54" />
    {/* head */}
    <path d="M42 26 L48 22 Q52 20 52 16 Q52 12 48 12 L44 12 Q40 12 40 16 L40 20" />
    {/* horns */}
    <path d="M44 12 L44 8" />
    <path d="M48 12 L48 8" />
    {/* eye */}
    <circle cx="46" cy="15" r="1" fill="currentColor" />
    {/* udder / belly line */}
    <path d="M30 44 Q32 48 34 44" />
  </svg>
);

// Rooster / bird alternative silhouette (chickens, roosters, fowl)
const RoosterSilhouette = ({ size = 96 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {/* body */}
    <path d="M22 46 Q18 40 20 32 Q22 24 30 22 L38 22 Q46 22 48 30 L50 40 L46 46 Z" />
    {/* tail feathers */}
    <path d="M46 32 Q54 24 52 16 Q48 18 44 24" />
    {/* head */}
    <path d="M38 22 Q40 16 44 16 Q46 16 46 20" />
    {/* comb */}
    <path d="M42 16 Q42 12 40 12 Q42 10 44 12" />
    <path d="M44 14 Q46 10 44 8" />
    {/* wattle */}
    <path d="M42 22 Q44 26 42 28" />
    {/* eye */}
    <circle cx="43" cy="20" r="0.8" fill="currentColor" />
    {/* legs */}
    <path d="M28 46 L28 54 M30 54 L32 54" />
    <path d="M38 46 L38 54 M40 54 L42 54" />
  </svg>
);

// Snake / reptile silhouette
const ReptileSilhouette = ({ size = 96 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 44 Q14 32 24 34 Q34 36 30 26 Q26 18 36 16 Q46 14 50 22 Q54 30 48 34 L50 32" />
    <circle cx="49" cy="21" r="1" fill="currentColor" />
    <path d="M52 22 L56 20 M52 22 L56 24" />
  </svg>
);

// "Multiple animals" — stacked paw + heads suggestion
const MultipleAnimals = ({ size = 96 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none"
       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {/* four toe pads */}
    <ellipse cx="20" cy="20" rx="4" ry="6" fill="currentColor" stroke="none" />
    <ellipse cx="30" cy="14" rx="4" ry="6" fill="currentColor" stroke="none" />
    <ellipse cx="40" cy="14" rx="4" ry="6" fill="currentColor" stroke="none" />
    <ellipse cx="50" cy="20" rx="4" ry="6" fill="currentColor" stroke="none" />
    {/* main paw pad */}
    <path d="M22 44 Q22 30 35 30 Q48 30 48 44 L48 48 Q48 54 42 54 L28 54 Q22 54 22 48 Z" fill="currentColor" stroke="none" />
  </svg>
);

// ---- Word regexes (all \b...\b boundaries so "bulldog" ≠ livestock etc.) ----
// CATTLE_WORDS intentionally does NOT include the bare word "bull" — common
// dog-breed phrases like "bull terrier", "bulldog", "pit bull" would otherwise
// trip it. We rely on "cattle / cows / calf / steer" for actual bovine cases.
const CATTLE_WORDS = /\b(cattle|cows?|calf|calves|steers?)\b/i;
const OTHER_LIVESTOCK = /\b(pigs?|hogs?|goats?|sheep|lambs?)\b/i;
const HORSE_WORDS = /\b(horses?|equine|pony|ponies|donkeys?|mules?|highlands?|stallions?|mares?)\b/i;
const DOG_WORDS = /\b(dogs?|puppy|puppies|pups?|pit ?bulls?|bulldogs?|bull ?terriers?|terriers?|(?:american |micro )?bully|bullies|pitties?|pittie|dachshunds?|beagles?|boxers?|mastiffs?|spaniels?|corgis?|pugs?|collies?|dobermans?|k-?9s?|canines?|malinois|dalmatians?|shepherds?|labs?|labradors?|huskies|husky|great pyrenees|rottweilers?|chihuahuas?|retrievers?|poodles?)\b/i;
const CAT_WORDS = /\b(cats?|kittens?|kitty|kitties|felines?|tabby|tabbies|ragdolls?|persians?)\b/i;
const BIRD_WORDS = /\b(birds?|chickens?|roosters?|ducks?|geese|goose|hens?|fowl|parrots?|cockfight(?:ing)?|songbirds?|crows?)\b/i;
const REPTILE_WORDS = /\b(snakes?|pythons?|ball ?pythons?|lizards?|reptiles?|turtles?|iguanas?|geckos?|tortoises?)\b/i;

function pickVariant(animalsText) {
  const t = String(animalsText || "").toLowerCase();
  if (!t) return "shield";
  const hits = [];
  if (DOG_WORDS.test(t))       hits.push("dog");
  if (CAT_WORDS.test(t))       hits.push("cat");
  if (HORSE_WORDS.test(t))     hits.push("horse");
  if (CATTLE_WORDS.test(t) || OTHER_LIVESTOCK.test(t)) hits.push("livestock");
  if (BIRD_WORDS.test(t))      hits.push("bird");
  if (REPTILE_WORDS.test(t))   hits.push("reptile");
  if (hits.length >= 2) return "multi";
  if (hits.length === 1) return hits[0];
  return "shield";
}

function VariantIcon({ variant, size = 96 }) {
  const cls = "text-zinc-700";
  switch (variant) {
    case "dog":       return <Dog size={size} className={cls} strokeWidth={1.6} />;
    case "cat":       return <Cat size={size} className={cls} strokeWidth={1.6} />;
    case "bird":      return <span className={cls}><RoosterSilhouette size={size} /></span>;
    case "reptile":   return <span className={cls}><ReptileSilhouette size={size} /></span>;
    case "horse":     return <span className={cls}><HorseSilhouette size={size} /></span>;
    case "livestock": return <span className={cls}><LivestockSilhouette size={size} /></span>;
    case "multi":     return <span className={cls}><MultipleAnimals size={size} /></span>;
    case "shield":
    default:          return <Shield size={size} className={cls} strokeWidth={1.4} />;
  }
}

function variantLabel(variant) {
  switch (variant) {
    case "dog":       return "Case involves dog(s)";
    case "cat":       return "Case involves cat(s)";
    case "horse":     return "Case involves horse(s)";
    case "livestock": return "Case involves livestock";
    case "bird":      return "Case involves bird(s) / fowl";
    case "reptile":   return "Case involves reptile(s)";
    case "multi":     return "Case involves multiple animals";
    default:          return "Public animal-cruelty record";
  }
}

/**
 * @param {string} animals     — free-text "animals_affected" value from the record
 * @param {("sm"|"md"|"lg")} size — panel size preset
 * @param {string} className   — extra classes
 * @param {string} testId      — data-testid override
 */
export default function PhotoUnavailablePlaceholder({
  animals = "",
  size = "md",
  className = "",
  testId = "photo-unavailable-placeholder",
}) {
  const variant = pickVariant(animals);
  // Icon sizes bumped ~30% and title weight/size increased per user feedback
  // 2026-09-24 so the panel stays readable on registry cards and mobile.
  const dims = size === "sm"
    ? { box: "h-48 sm:h-52", icon: 80,  title: "text-base sm:text-lg",  sub: "text-xs sm:text-sm" }
    : size === "lg"
      ? { box: "h-80 sm:h-96", icon: 168, title: "text-xl sm:text-2xl", sub: "text-sm sm:text-base" }
      : { box: "h-64 sm:h-72", icon: 128, title: "text-lg sm:text-xl",  sub: "text-sm" };

  return (
    <div
      className={`relative ${dims.box} w-full max-w-sm bg-zinc-100 border border-zinc-900 overflow-hidden ${className}`}
      data-testid={testId}
      data-variant={variant}
      role="img"
      aria-label={`Public record — photo unavailable. ${variantLabel(variant)}.`}
    >
      {/* subtle grid pattern for texture — never looks like a broken img */}
      <div
        className="absolute inset-0 opacity-[0.045] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #09090b 0 1px, transparent 1px 24px), repeating-linear-gradient(90deg, #09090b 0 1px, transparent 1px 24px)",
        }}
      />

      {/* corner "PAWS ON RECORD" watermark */}
      <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-500">
        <Shield size={11} strokeWidth={2.5} />
        Paws on Record
      </div>

      {/* centered variant silhouette */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-5 text-center">
        <VariantIcon variant={variant} size={dims.icon} />
        <div className={`mt-4 font-display font-black tracking-tight text-zinc-900 ${dims.title} leading-tight`}>
          Public Record • Photo Unavailable
        </div>
        <div className={`mt-1.5 text-zinc-700 ${dims.sub} font-semibold leading-snug max-w-[280px]`}>
          {variantLabel(variant)}
        </div>
      </div>

      {/* bottom red bar — the intentional, PAWS-branded footer */}
      <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-red-600" />
    </div>
  );
}
